import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  type OnChangeFn,
  type PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from './button.js';
import { Label } from './label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select.js';
import { Skeleton } from './skeleton.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table.js';

export type { ColumnDef, PaginationState };

export type onPaginationChange = OnChangeFn<PaginationState>;
export type onPrefetchNext = (nextPagination: PaginationState) => void;

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  pagination?: PaginationState;
  onPaginationChange?: onPaginationChange;
  totalItems?: number;
  emptyMessage?: string;
  isLoading?: boolean;
  onPrefetchNext?: onPrefetchNext;
  searchValue?: string;
  searchField?: string[];
  rowsPerPageText?: string;
  paginationText?: (props: { pageIndex: number; pageCount: number }) => string;
}

const fallbackData: unknown[] = [];

export function DataTable<TData, TValue>({
  columns,
  data,
  pagination: externalPagination,
  onPaginationChange,
  totalItems,
  emptyMessage = 'No results.',
  isLoading = false,
  onPrefetchNext,
  searchField,
  searchValue,
  rowsPerPageText = 'Rows per page',
  paginationText = ({ pageIndex, pageCount }) =>
    `Page ${pageIndex + 1} of ${pageCount}`,
}: DataTableProps<TData, TValue>) {
  const [internalPagination, setInternalPagination] = useState<PaginationState>(
    {
      pageIndex: 0,
      pageSize: 10,
    },
  );

  const pagination = externalPagination || internalPagination;
  const setPagination = onPaginationChange || setInternalPagination;

  const isExternalPagination = !!externalPagination;

  const globalFilterFn = useMemo(() => {
    return (row: any, _columnId: string, value: string) => {
      if (!searchField || !value) return true;

      const searchFields = Array.isArray(searchField)
        ? searchField
        : [searchField];
      const searchTerm = value.toLowerCase();

      return searchFields.some((field) => {
        const cellValue = row.getValue(field);
        if (cellValue == null) return false;
        return String(cellValue).toLowerCase().includes(searchTerm);
      });
    };
  }, [searchField]);

  const table = useReactTable({
    data: data ?? fallbackData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    manualPagination: isExternalPagination,
    pageCount: totalItems ? Math.ceil(totalItems / pagination.pageSize) : -1,
    globalFilterFn,
    state: {
      pagination,
      globalFilter: searchValue,
    },
  });

  // Prefetch next page when data loads and external pagination is enabled
  useEffect(() => {
    if (
      isExternalPagination &&
      onPrefetchNext &&
      !isLoading &&
      data &&
      totalItems
    ) {
      const nextPageIndex = pagination.pageIndex + 1;
      const maxPageIndex = Math.ceil(totalItems / pagination.pageSize) - 1;

      // Only prefetch if there's a next page available
      if (nextPageIndex <= maxPageIndex) {
        const nextPagination = {
          pageIndex: nextPageIndex,
          pageSize: pagination.pageSize,
        };
        onPrefetchNext(nextPagination);
      }
    }
  }, [
    isExternalPagination,
    onPrefetchNext,
    isLoading,
    data,
    pagination.pageIndex,
    pagination.pageSize,
    totalItems,
  ]);

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {/* @ts-ignore */}
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: pagination.pageSize }).map((_, index) => (
                <TableRow key={`skeleton-row-${index}`}>
                  {columns.map((_, colIndex) => (
                    <TableCell key={`skeleton-cell-${index}-${colIndex}`}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {/* @ts-ignore */}
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between py-4">
        <div className="text-muted-foreground hidden flex-1 text-sm lg:flex" />
        <div className="flex w-full items-center gap-8 lg:w-fit">
          <div className="hidden items-center gap-2 lg:flex">
            <Label htmlFor="rows-per-page" className="text-sm font-medium">
              {rowsPerPageText}
            </Label>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger size="sm" className="w-20" id="rows-per-page">
                <SelectValue
                  placeholder={table.getState().pagination.pageSize}
                />
              </SelectTrigger>
              <SelectContent side="top">
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-fit items-center justify-center text-sm font-medium">
            {paginationText({
              pageIndex: table.getState().pagination.pageIndex,
              pageCount: table.getPageCount(),
            })}
          </div>
          <div className="ml-auto flex items-center gap-2 lg:ml-0">
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Go to first page</span>
              {/* @ts-ignore */}
              <ChevronsLeft />
            </Button>
            <Button
              variant="outline"
              className="size-8"
              size="icon"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Go to previous page</span>
              {/* @ts-ignore */}
              <ChevronLeft />
            </Button>
            <Button
              variant="outline"
              className="size-8"
              size="icon"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">Go to next page</span>
              {/* @ts-ignore */}
              <ChevronRight />
            </Button>
            <Button
              variant="outline"
              className="hidden size-8 lg:flex"
              size="icon"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">Go to last page</span>
              {/* @ts-ignore */}
              <ChevronsRight />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
